import React, { useEffect, useMemo } from "react";
import { View, StyleSheet } from "react-native";
import { useNavigation, useRoute } from "@react-navigation/native";
import {
  QuestionLayout,
  IconInput,
  MedicalHistoryTextField,
} from "@src/packages/medical-history/components";
import { MoonNumericField, MoonSelectField, DateField } from "@louise/moon-ui";
import { FileText } from "lucide-react-native";
import { useTranslations } from "@src/packages/i18n";
import {
  useSubstances,
  SubstanceFrequency,
  SubstanceType,
  SubstancesFlowRoute,
} from "../SubstancesContext";
import type { SubstancesStackScreenProps } from "../SubstancesNavigator";
import { SUBSTANCE_FREQUENCY_OPTIONS, SUBSTANCE_DETAIL_COPY } from "../options";
import { buildSubstancesNavigationStack } from "../buildSubstancesNavigationStack";

export function SubstanceDetailsScreen() {
  const t = useTranslations();
  const navigation =
    useNavigation<
      SubstancesStackScreenProps<"SubstanceDetails">["navigation"]
    >();
  const route =
    useRoute<SubstancesStackScreenProps<"SubstanceDetails">["route"]>();
  const routeSubstance = (
    route.params as { substance?: SubstanceType } | undefined
  )?.substance;

  const { state, updateState } = useSubstances();

  const stack = state.navigationStack?.length
    ? state.navigationStack
    : buildSubstancesNavigationStack(state);

  // The substance is carried in navigation state, which a reload of this step
  // does not restore. Falling back to the first substance this flow's own
  // stack details here keeps the step on the record instead of on nothing.
  const substance =
    routeSubstance ??
    stack.find(
      (routeDef): routeDef is Extract<SubstancesFlowRoute, { type: string }> =>
        typeof routeDef === "object" && routeDef.type === "SubstanceDetails",
    )?.substance;

  useEffect(() => {
    if (routeSubstance || !substance) return;
    navigation.setParams({ substance });
  }, [routeSubstance, substance, navigation]);

  const detail = useMemo(
    () => state.substanceDetails.find((item) => item.substance === substance),
    [state.substanceDetails, substance],
  );

  useEffect(() => {
    if (substance && !detail) {
      updateState({
        substanceDetails: state.substanceDetails.concat({
          id: undefined,
          substance,
          frequency: null,
          averageQuantity: null,
          otherFrequency: null,
          startDate: null,
          stopDate: null,
          notes: null,
          other: null,
        }),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail, substance]);

  const copy =
    SUBSTANCE_DETAIL_COPY[substance as keyof typeof SUBSTANCE_DETAIL_COPY];

  const updateDetail = (partial: {
    frequency?: SubstanceFrequency | null;
    averageQuantity?: number | null;
    otherFrequency?: string | null;
    startDate?: string | null;
    stopDate?: string | null;
    notes?: string | null;
    other?: string | null;
  }) => {
    updateState({
      substanceDetails: state.substanceDetails.map((item) => {
        if (item.substance === substance) {
          return { ...item, ...partial };
        }
        return item;
      }),
    });
  };

  const currentIndex = stack.findIndex((routeDef) => {
    if (typeof routeDef === "object") {
      return (
        routeDef.type === "SubstanceDetails" && routeDef.substance === substance
      );
    }
    return false;
  });

  const nextRoute = currentIndex >= 0 ? stack[currentIndex + 1] : undefined;

  const handleSave = () => {
    if (!nextRoute) {
      navigation.getParent()?.goBack();
      return;
    }

    const nextIndex = Math.min(currentIndex + 1, stack.length - 1);
    updateState({
      navigationStack: stack,
      currentNavigationIndex: nextIndex,
    });

    if (typeof nextRoute === "object") {
      navigation.navigate("SubstanceDetails", {
        substance: nextRoute.substance,
      });
    } else {
      navigation.navigate(nextRoute);
    }
  };

  const frequencyValue = detail?.frequency ?? null;
  const quantityValue =
    typeof detail?.averageQuantity === "number" ? detail.averageQuantity : null;
  const notesValue = detail?.notes ?? "";
  const otherValue = detail?.other ?? "";
  const startDateValue = detail?.startDate ?? "";
  const stopDateValue = detail?.stopDate ?? "";

  const showOtherField = substance === "other";

  if (!copy) return null;

  return (
    <QuestionLayout
      title={t(copy.titleKey)}
      subtitle={t("med_history.lifestyle.substances.details_subtitle")}
      onBack={() => navigation.goBack()}
      onSave={handleSave}
      saveDisabled={!frequencyValue}
      saveLabel={t("med_history.lifestyle.continue")}
    >
      <View style={styles.formContainer}>
        <View style={styles.inputGroup}>
          <MoonSelectField
            label={t("med_history.lifestyle.substances.frequency_label")}
            placeholder={t(
              "med_history.lifestyle.substances.frequency_placeholder",
            )}
            value={frequencyValue}
            onChange={(value) =>
              updateDetail({
                frequency: (value as SubstanceFrequency | null) ?? null,
              })
            }
            options={SUBSTANCE_FREQUENCY_OPTIONS.map((option) => ({
              label: t(option.labelKey),
              value: option.id,
            }))}
          />
        </View>

        <View style={styles.inputGroup}>
          <MoonNumericField
            label={t(copy.quantityLabelKey)}
            placeholder={t(
              "med_history.lifestyle.substances.quantity_placeholder",
            )}
            value={quantityValue}
            decimals={1}
            onChange={(value) => updateDetail({ averageQuantity: value })}
          />
        </View>

        <View style={styles.inputGroup}>
          <MedicalHistoryTextField
            label={t(copy.contextLabelKey)}
            icon={FileText}
            placeholder={t(
              "med_history.lifestyle.substances.notes_placeholder",
            )}
            value={notesValue}
            onChangeText={(text) =>
              updateDetail({ notes: text.length > 0 ? text : null })
            }
            multiline
            numberOfLines={3}
          />
        </View>

        {showOtherField ? (
          <View style={styles.inputGroup}>
            <IconInput
              label={t("med_history.lifestyle.substances.other_label")}
              icon={FileText}
              placeholder={t(
                "med_history.lifestyle.substances.other_placeholder",
              )}
              value={otherValue}
              onChangeText={(text) =>
                updateDetail({ other: text.length > 0 ? text : null })
              }
            />
          </View>
        ) : null}

        <View style={styles.inputGroup}>
          <DateField
            label={t("med_history.lifestyle.substances.start_date_label")}
            value={startDateValue}
            onChange={(iso) => updateDetail({ startDate: iso || null })}
          />
        </View>

        <View style={styles.inputGroup}>
          <DateField
            label={t("med_history.lifestyle.substances.stop_date_label")}
            value={stopDateValue}
            onChange={(iso) => updateDetail({ stopDate: iso || null })}
          />
        </View>
      </View>
    </QuestionLayout>
  );
}

const styles = StyleSheet.create({
  formContainer: {
    width: "100%",
    marginBottom: 40,
  },
  inputGroup: {
    marginBottom: 16,
  },
});
